import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ClipboardPlus, PackagePlus, Calendar, Layers, Weight, Clock, Search, Check, AlertCircle, ChevronDown, Thermometer, Package, Wrench } from 'lucide-react';
import { useCreatePO } from '../hooks/useCreatePO';
import { useUIStore } from '../hooks/useUIStore';
import { useAuthStore } from '../hooks/useAuth';
import useSWR from 'swr';

// Fallback debounce hook if package is missing
function useDebounceValue<T>(value: T, delay: number): T {
    const [debouncedValue, setDebouncedValue] = useState<T>(value);
    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);
        return () => {
            clearTimeout(handler);
        };
    }, [value, delay]);
    return debouncedValue;
}

export function POCreationWizard() {
    const { isPOWizardOpen, togglePOWizard } = useUIStore();
    const { token } = useAuthStore();
    const { mutate, isLoading } = useCreatePO();

    const onClose = () => togglePOWizard(false);
    const onSuccess = (_data?: any) => { };

    // --- Form State ---
    const [poNumber, setPoNumber] = useState('');

    // Customer / Client State
    const [clientName, setClientName] = useState('');
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(null);
    const [isClientSearchOpen, setIsClientSearchOpen] = useState(false);
    const [creatingCustomer, setCreatingCustomer] = useState(false);
    const [customerCreateError, setCustomerCreateError] = useState<string | null>(null);
    const debouncedClientSearch = useDebounceValue(clientName, 300);

    // PO Details
    const [type, setType] = useState<'inbound' | 'outbound'>('inbound');
    const [arrivalDate, setArrivalDate] = useState('');
    const [arrivalTime, setArrivalTime] = useState(''); // Optional, UI only
    const [priority, setPriority] = useState<'standard' | 'urgent' | 'critical'>('standard');

    // Cargo Details
    // Cargo details (cargoType removed — inferred from temperature selection)
    const [skids, setSkids] = useState('');
    const [weight, setWeight] = useState(''); // Optional
    const [weightUnit, setWeightUnit] = useState<'lbs' | 'kg'>('lbs'); // Optional

    // Extra
    const [referenceNumber, setReferenceNumber] = useState(''); // Optional
    const [notes, setNotes] = useState(''); // Optional
    const [advancedOpen, setAdvancedOpen] = useState(false); // Additional details collapsed by default

    // Phase INV-1: Receiving & Putaway Fields
    const [requiredTemperature, setRequiredTemperature] = useState('');
    const [caseCount, setCaseCount] = useState('');
    const [itemDescription, setItemDescription] = useState('');
    const [handlingType, setHandlingType] = useState('');
    const [putawayZone, setPutawayZone] = useState('');
    const [receivingDetailsOpen, setReceivingDetailsOpen] = useState(false);

    // Fetcher using store token
    const fetcher = (url: string) => {
        return fetch(url, {
            headers: {
                'Authorization': `Bearer ${token}`
            }
        }).then(res => res.json());
    };

    // Customer Autocomplete Data
    const shouldFetch = isPOWizardOpen && debouncedClientSearch.length > 1;
    const { data: customerData, mutate: mutateCustomers } = useSWR(
        shouldFetch ? `/api/customers?query=${encodeURIComponent(debouncedClientSearch.trim())}` : null,
        fetcher
    );

    const PO_DRAFT_STORAGE_KEY = 'baywhiz_po_wizard_draft_v1';

    // Restore saved draft when wizard opens (prevents D17 data loss across navigation)
    useEffect(() => {
        if (!isPOWizardOpen) return;
        try {
            const raw = sessionStorage.getItem(PO_DRAFT_STORAGE_KEY);
            if (!raw) return;
            const d = JSON.parse(raw);
            if (d.poNumber && !poNumber) setPoNumber(d.poNumber);
            if (d.clientName && !clientName) setClientName(d.clientName);
            if (d.selectedCustomerId && !selectedCustomerId) setSelectedCustomerId(d.selectedCustomerId);
            if (d.type) setType(d.type);
            if (d.arrivalDate && !arrivalDate) setArrivalDate(d.arrivalDate);
            if (d.priority) setPriority(d.priority);
            if (d.skids && !skids) setSkids(d.skids);
            if (d.weight && !weight) setWeight(d.weight);
            if (d.notes && !notes) setNotes(d.notes);
        } catch { /* ignore storage parse errors */ }
    }, [isPOWizardOpen]);

    // Persist draft while wizard is open
    useEffect(() => {
        if (!isPOWizardOpen) return;
        try {
            if (poNumber || clientName || arrivalDate || skids || weight || notes) {
                sessionStorage.setItem(PO_DRAFT_STORAGE_KEY, JSON.stringify({
                    poNumber,
                    clientName,
                    selectedCustomerId,
                    type,
                    arrivalDate,
                    priority,
                    skids,
                    weight,
                    notes,
                }));
            }
        } catch { /* ignore */ }
    }, [isPOWizardOpen, poNumber, clientName, selectedCustomerId, type, arrivalDate, priority, skids, weight, notes]);

    const handleQuickCreateCustomer = async () => {
        const trimmed = clientName.trim();
        if (!trimmed || !token || creatingCustomer) return;
        setCreatingCustomer(true);
        setCustomerCreateError(null);
        try {
            const res = await fetch('/api/customers', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({ name: trimmed }),
            });
            const data = await res.json();
            if (res.status === 409 && data?.conflictCustomerId) {
                setSelectedCustomerId(data.conflictCustomerId);
                setClientName(data.conflictValue || trimmed);
                setIsClientSearchOpen(false);
                mutateCustomers();
                return;
            }
            if (!res.ok) {
                throw new Error(data?.error || 'Failed to create customer');
            }
            setSelectedCustomerId(data.id);
            setClientName(data.name || trimmed);
            setIsClientSearchOpen(false);
            mutateCustomers();
        } catch (err: any) {
            setCustomerCreateError(err.message || 'Could not create customer');
        } finally {
            setCreatingCustomer(false);
        }
    };

    const generatePONumber = () => {
        const now = new Date();
        const yy = now.getFullYear().toString().slice(-2);
        const mm = String(now.getMonth() + 1).padStart(2, '0');
        const dd = String(now.getDate()).padStart(2, '0');
        const hh = String(now.getHours()).padStart(2, '0');
        const min = String(now.getMinutes()).padStart(2, '0');
        const ss = String(now.getSeconds()).padStart(2, '0');
        setPoNumber(`PO-${yy}${mm}${dd}-${hh}${min}${ss}`);
    };

    const handleClientSelect = (name: string, id: string) => {
        setClientName(name);
        setSelectedCustomerId(id);
        setIsClientSearchOpen(false);
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!clientName || !poNumber) return;

        // Construct Description from optional fields
        const descriptionParts = [];
        // If temperature is set, mark as temp-controlled
        if (requiredTemperature && requiredTemperature !== 'Ambient') descriptionParts.push(`Temp: ${requiredTemperature}`);

        if (weight !== '' && Number(weight) > 0) {
            descriptionParts.push(`Weight: ${weight} ${weightUnit}`);
        }

        if (referenceNumber) descriptionParts.push(`Ref: ${referenceNumber}`);
        if (arrivalTime) descriptionParts.push(`Window: ${arrivalTime}`);
        if (notes) descriptionParts.push(notes);

        const finalDescription = descriptionParts.join(' | ');

        try {
            await mutate({
                customerPoNumber: poNumber,
                clientName,
                customerId: selectedCustomerId || undefined,
                type,
                totalSkids: skids !== '' ? Number(skids) : 0,
                totalWeight: weight !== '' ? Number(weight) : 0, // Backend now supports this
                description: finalDescription,
                // Phase INV-1: Receiving & Putaway
                required_temperature: requiredTemperature || undefined,
                case_count: caseCount !== '' ? Number(caseCount) : undefined,
                item_description: itemDescription || undefined,
                handling_type: handlingType || undefined,
                putaway_zone: putawayZone || undefined,
                dueDate: arrivalDate || undefined,
                priority,
            }, {
                onSuccess: (data) => {
                    try { sessionStorage.removeItem(PO_DRAFT_STORAGE_KEY); } catch { /* ignore */ }
                    onSuccess(data);
                    onClose();
                    // Reset Form
                    setClientName('');
                    setSelectedCustomerId(null);
                    setPoNumber('');
                    setSkids('');
                    setWeight('');
                    setNotes('');
                    setReferenceNumber('');
                    setArrivalTime('');
                    setArrivalDate('');
                    setPriority('standard');

                    // Additional resets (cargoType removed)
                    setWeightUnit('lbs');
                    setIsClientSearchOpen(false);

                    // Phase INV-1 resets
                    setRequiredTemperature('');
                    setCaseCount('');
                    setItemDescription('');
                    setHandlingType('');
                    setPutawayZone('');
                    setReceivingDetailsOpen(false);
                }
            });
        } catch (err: any) {
            // The useCreatePO hook already toasts, but we can customize or focus field here
            // If the error message mentions duplicate, we just leave the modal open
            if (err.message?.toLowerCase().includes('duplicate po number') || err.message?.toLowerCase().includes('already exists')) {
                // We keep the modal open and just return early
                // optionally we could focus the poNumber input here if we had a ref
                return;
            }
            // For other errors, useCreatePO handles the toast
        }
    };

    // Close search on click outside
    const searchRef = useRef<HTMLDivElement>(null);
    useEffect(() => {
        function handleClickOutside(event: MouseEvent) {
            if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
                setIsClientSearchOpen(false);
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const hasResults = customerData?.customers?.length > 0;
    const showNoResults = isClientSearchOpen && shouldFetch && customerData && !hasResults;

    return (
        <AnimatePresence>
            {isPOWizardOpen && (
                <div className="fixed inset-0 z-[200] flex items-end sm:items-center justify-center sm:p-4 p-0">
                    {/* Backdrop */}
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        onClick={onClose}
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
                    />

                    {/* Modal Window */}
                    <motion.div
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 40 }}
                        className="relative w-full sm:max-w-4xl bg-white dark:bg-slate-900/95 backdrop-blur-xl border border-slate-200 dark:border-primary/50 sm:rounded-2xl rounded-t-2xl rounded-b-none shadow-xl dark:shadow-[0_0_50px_rgba(8,145,178,0.2)] flex flex-col max-h-[95dvh] sm:max-h-[90vh]"
                    >
                        {/* Header */}
                        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 shrink-0">
                            <div>
                                <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white font-outfit flex items-center gap-2">
                                    <ClipboardPlus className="text-primary" />
                                    New Purchase Order
                                </h2>
                                <p className="text-slate-500 dark:text-slate-400 text-xs sm:text-sm mt-0.5">Start with the PO number and what is arriving. You can fill in extra details later.</p>
                            </div>
                            <button onClick={onClose} className="text-slate-400 hover:text-slate-900 dark:text-slate-500 dark:hover:text-white transition-colors">
                                <X size={24} />
                            </button>
                        </div>

                        {/* Scrollable Form Area */}
                        <div className="overflow-y-auto p-4 sm:p-6 custom-scrollbar">
                            <form id="create-po-form" onSubmit={handleSubmit} className="space-y-8">

                                {/* SECTION 1: REQUIRED FIELDS */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-white/5">
                                        <div className="h-6 w-1 bg-primary rounded-full"></div>
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Required</h3>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        {/* PO Number */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">PO Number *</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="text"
                                                    value={poNumber}
                                                    onChange={(e) => setPoNumber(e.target.value.toUpperCase())}
                                                    placeholder="Required"
                                                    className="flex-1 bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white font-mono focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 focus:placeholder:text-slate-500"
                                                />
                                                <button
                                                    type="button"
                                                    onClick={generatePONumber}
                                                    className="px-3 py-2 bg-slate-100 dark:bg-white/5 hover:bg-slate-200 dark:hover:bg-white/10 border border-slate-300 dark:border-white/10 rounded-lg text-slate-700 dark:text-slate-300 text-xs font-semibold transition-colors whitespace-nowrap"
                                                >
                                                    Auto-Gen
                                                </button>
                                            </div>
                                        </div>

                                        {/* Supplier / Client Autocomplete */}
                                        <div className="space-y-1.5 relative" ref={searchRef}>
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Supplier / Client *</label>
                                            <div className="relative">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                <input
                                                    type="text"
                                                    value={clientName}
                                                    onChange={(e) => {
                                                        setClientName(e.target.value);
                                                        setSelectedCustomerId(null); // Clear explicit link on free-text edit
                                                        setIsClientSearchOpen(true);
                                                    }}
                                                    onFocus={() => setIsClientSearchOpen(true)}
                                                    placeholder="Search or enter name..."
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                />
                                            </div>

                                            {/* Autocomplete Dropdown */}
                                            {isClientSearchOpen && (
                                                <>
                                                    {hasResults && (
                                                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg shadow-2xl overflow-hidden max-h-60 overflow-y-auto">
                                                            {customerData.customers.map((c: any) => (
                                                                <button
                                                                    key={c.id}
                                                                    type="button"
                                                                    onClick={() => handleClientSelect(c.name, c.id)}
                                                                    className="w-full text-left px-4 py-3 hover:bg-slate-50 dark:hover:bg-white/5 transition-colors flex items-center justify-between group"
                                                                >
                                                                    <div>
                                                                        <div className="text-slate-900 dark:text-white font-medium text-sm">{c.name}</div>
                                                                        {c.code && <div className="text-xs text-slate-500 font-mono">{c.code}</div>}
                                                                    </div>
                                                                    {clientName === c.name && <Check size={14} className="text-primary" />}
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                    {showNoResults && (
                                                        <div className="absolute z-50 top-full left-0 right-0 mt-1 bg-white dark:bg-slate-900 border border-slate-200 dark:border-white/10 rounded-lg shadow-2xl overflow-hidden px-4 py-3">
                                                            <div className="text-slate-500 text-sm italic">No matches found</div>
                                                        </div>
                                                    )}
                                                </>
                                            )}

                                            {/* Customer linkage validation hint */}
                                            {clientName && !selectedCustomerId && (
                                                <div className="mt-1.5 space-y-1">
                                                    <p className="text-xs text-amber-600 dark:text-amber-400 flex flex-wrap items-center gap-1.5">
                                                        <AlertCircle size={12} />
                                                        <span>Select a customer from the list, or</span>
                                                        <button
                                                            type="button"
                                                            disabled={creatingCustomer}
                                                            onClick={handleQuickCreateCustomer}
                                                            className="underline font-semibold hover:text-amber-700 dark:hover:text-amber-300 disabled:opacity-50"
                                                        >
                                                            {creatingCustomer ? 'Creating…' : `+ Create "${clientName.trim()}" now`}
                                                        </button>
                                                        <span>without leaving this form.</span>
                                                    </p>
                                                    {customerCreateError && (
                                                        <p className="text-xs text-red-500">{customerCreateError}</p>
                                                    )}
                                                </div>
                                            )}
                                            {selectedCustomerId && (
                                                <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-1.5 flex items-center gap-1">
                                                    <Check size={12} /> Customer linked
                                                </p>
                                            )}
                                        </div>

                                        {/* Direction */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Direction</label>
                                            <div className="flex bg-slate-100 dark:bg-black/40 p-1 rounded-lg border border-slate-300 dark:border-white/10">
                                                <button
                                                    type="button"
                                                    onClick={() => setType('inbound')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${type === 'inbound' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                                >
                                                    INBOUND
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setType('outbound')}
                                                    className={`flex-1 py-1.5 text-xs font-bold rounded-md transition-all ${type === 'outbound' ? 'bg-blue-100 dark:bg-blue-500/20 text-blue-600 dark:text-blue-400 shadow-sm' : 'text-slate-500 hover:text-slate-700 dark:hover:text-slate-300'}`}
                                                >
                                                    OUTBOUND
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* SECTION 2: OPTIONAL — FREIGHT & SCHEDULE */}
                                <div className="space-y-4">
                                    <div className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-white/5">
                                        <div className="h-6 w-1 bg-blue-500 rounded-full"></div>
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider">Freight & Schedule</h3>
                                        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded-full">Optional</span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                                        {/* Due Date */}
                                        <div className="space-y-1.5">
                                            <span className="text-[10px] font-semibold text-blue-500 dark:text-blue-400 uppercase tracking-widest">Arrival Requirement</span>
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter block">Required Arrival Date</label>
                                            <div className="relative">
                                                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                <input
                                                    type="date"
                                                    value={arrivalDate}
                                                    onChange={(e) => setArrivalDate(e.target.value)}
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm [color-scheme:light] dark:[color-scheme:dark]"
                                                />
                                            </div>
                                        </div>

                                        {/* Priority */}
                                        <div className="space-y-1.5">
                                            <span className="text-[10px] font-semibold text-transparent uppercase tracking-widest select-none">&nbsp;</span>
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter block">Priority</label>
                                            <div className="relative">
                                                <select
                                                    value={priority}
                                                    onChange={(e) => setPriority(e.target.value as any)}
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-2.5 pr-10 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm cursor-pointer"
                                                >
                                                    <option value="standard">Standard</option>
                                                    <option value="urgent">⚡ Urgent</option>
                                                    <option value="critical">🔴 Critical</option>
                                                </select>
                                                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" size={16} />
                                            </div>
                                        </div>

                                        {/* Skids */}
                                        <div className="space-y-1.5">
                                            <span className="text-[10px] font-semibold text-transparent uppercase tracking-widest select-none">&nbsp;</span>
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter block">Total Skids</label>
                                            <div className="relative">
                                                <Layers className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                <input
                                                    type="number"
                                                    value={skids}
                                                    onChange={(e) => setSkids(e.target.value)}
                                                    min="0"
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                />
                                            </div>
                                        </div>

                                        {/* Weight (Optional) */}
                                        <div className="space-y-1.5">
                                            <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Weight</label>
                                            <div className="flex gap-2">
                                                <div className="relative flex-1">
                                                    <Weight className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                    <input
                                                        type="number"
                                                        value={weight}
                                                        onChange={(e) => setWeight(e.target.value)}
                                                        min="0"
                                                        placeholder="0"
                                                        className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                    />
                                                </div>
                                                <select
                                                    value={weightUnit}
                                                    onChange={(e) => setWeightUnit(e.target.value as any)}
                                                    className="bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-3 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none text-sm w-20"
                                                >
                                                    <option value="lbs">lbs</option>
                                                    <option value="kg">kg</option>
                                                </select>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* SECTION: WAREHOUSE DETAILS (Merged Receiving + Handling, collapsed by default) */}
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setReceivingDetailsOpen(o => !o)}
                                        className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-white/5 w-full text-left"
                                    >
                                        <div className="h-6 w-1 bg-teal-500 rounded-full"></div>
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex-1">Warehouse Details</h3>
                                        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded-full mr-2">Optional</span>
                                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${receivingDetailsOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {receivingDetailsOpen && (
                                        <div className="space-y-4 mt-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Preferred Time Window */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Pref. Time Window</label>
                                                    <div className="relative">
                                                        <Clock className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                        <input
                                                            type="time"
                                                            value={arrivalTime}
                                                            onChange={(e) => setArrivalTime(e.target.value)}
                                                            className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm [color-scheme:light] dark:[color-scheme:dark]"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Temperature */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Temperature</label>
                                                    <div className="relative">
                                                        <Thermometer className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                        <select
                                                            value={requiredTemperature}
                                                            onChange={(e) => setRequiredTemperature(e.target.value)}
                                                            className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm appearance-none"
                                                        >
                                                            <option value="">Ambient (default)</option>
                                                            <option value="Chilled">Chilled</option>
                                                            <option value="Frozen">Frozen</option>
                                                        </select>
                                                    </div>
                                                </div>

                                                {/* Case Count */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Case Count</label>
                                                    <div className="relative">
                                                        <Package className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                        <input
                                                            type="number"
                                                            value={caseCount}
                                                            onChange={(e) => setCaseCount(e.target.value)}
                                                            min="0"
                                                            placeholder="0"
                                                            className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                        />
                                                    </div>
                                                    <p className="text-[11px] text-slate-400 dark:text-slate-500">Helps estimate unload time</p>
                                                </div>

                                                {/* Handling Type */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Handling</label>
                                                    <div className="relative">
                                                        <Wrench className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" size={16} />
                                                        <select
                                                            value={handlingType}
                                                            onChange={(e) => setHandlingType(e.target.value)}
                                                            className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg pl-10 pr-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm appearance-none"
                                                        >
                                                            <option value="">Select...</option>
                                                            <option value="Forklift">Forklift</option>
                                                            <option value="Pallet jack">Pallet jack</option>
                                                            <option value="Clamp">Clamp</option>
                                                            <option value="Hand unload">Hand unload</option>
                                                        </select>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Item Description */}
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Item Description</label>
                                                <textarea
                                                    value={itemDescription}
                                                    onChange={(e) => setItemDescription(e.target.value)}
                                                    placeholder="Example: Frozen vegetables, retail displays, packaging material"
                                                    rows={2}
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600 resize-none"
                                                />
                                            </div>

                                            {/* Putaway Zone */}
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Putaway Zone</label>
                                                <input
                                                    type="text"
                                                    value={putawayZone}
                                                    onChange={(e) => setPutawayZone(e.target.value)}
                                                    placeholder="e.g. Freezer A, Rack B3, Bulk floor"
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* SECTION 3: ADDITIONAL DETAILS (collapsed by default) */}
                                <div>
                                    <button
                                        type="button"
                                        onClick={() => setAdvancedOpen(o => !o)}
                                        className="flex items-center gap-2 pb-2 border-b border-slate-200 dark:border-white/5 w-full text-left"
                                    >
                                        <div className="h-6 w-1 bg-purple-500 rounded-full"></div>
                                        <h3 className="text-sm font-bold text-slate-800 dark:text-white uppercase tracking-wider flex-1">Additional Details</h3>
                                        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500 bg-slate-100 dark:bg-white/5 px-2 py-0.5 rounded-full mr-2">Optional</span>
                                        <ChevronDown className={`w-4 h-4 text-slate-400 transition-transform ${advancedOpen ? 'rotate-180' : ''}`} />
                                    </button>
                                    {advancedOpen && (
                                        <div className="space-y-4 mt-4">
                                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                                {/* Reference / BOL */}
                                                <div className="space-y-1.5">
                                                    <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Reference / BOL #</label>
                                                    <input
                                                        type="text"
                                                        value={referenceNumber}
                                                        onChange={(e) => setReferenceNumber(e.target.value)}
                                                        placeholder="You can add this later"
                                                        className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-2.5 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all placeholder:text-slate-400 dark:placeholder:text-slate-600 text-sm"
                                                    />
                                                </div>
                                            </div>
                                            <div className="space-y-1.5">
                                                <label className="text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider font-inter">Notes & Instructions</label>
                                                <textarea
                                                    value={notes}
                                                    onChange={(e) => setNotes(e.target.value)}
                                                    placeholder="Special handling, driver notes, gate codes…"
                                                    rows={2}
                                                    className="w-full bg-slate-100 dark:bg-black/40 border border-slate-300 dark:border-white/10 rounded-lg px-4 py-3 text-slate-900 dark:text-white focus:border-primary focus:ring-1 focus:ring-primary outline-none transition-all text-sm placeholder:text-slate-400 dark:placeholder:text-slate-600 resize-none"
                                                />
                                            </div>
                                        </div>
                                    )}
                                </div>

                            </form>
                        </div>

                        {/* Footer Actions */}
                        <div className="p-4 sm:p-6 border-t border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-white/5 flex flex-col-reverse sm:flex-row sm:justify-between items-stretch sm:items-center gap-3 shrink-0">
                            <div className="text-xs text-slate-400 dark:text-slate-500 hidden sm:block">
                                You can edit this later
                            </div>
                            <div className="flex flex-col sm:flex-row gap-3">
                                <button
                                    type="button"
                                    onClick={onClose}
                                    className="w-full sm:w-auto px-6 py-3 bg-slate-100 hover:bg-slate-200 dark:bg-white/5 dark:hover:bg-white/10 text-slate-800 dark:text-white rounded-xl font-bold transition-all border border-slate-300 dark:border-transparent dark:hover:border-white/10"
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    form="create-po-form"
                                    disabled={isLoading || !selectedCustomerId || !poNumber}
                                    className="w-full sm:w-auto px-8 py-3 bg-primary hover:bg-primary-600 disabled:opacity-50 disabled:cursor-not-allowed text-white rounded-xl font-bold transition-all shadow-[0_0_20px_rgba(8,145,178,0.3)] hover:shadow-[0_0_30px_rgba(8,145,178,0.5)] flex items-center justify-center gap-2"
                                >
                                    {isLoading ? (
                                        <span className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></span>
                                    ) : (
                                        <>
                                            <PackagePlus size={20} />
                                            Create PO
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
}
